import { useState, useRef, useEffect } from "react";

// ── SA Tax Knowledge Base ──────────────────────────────────────────────────
const KB = [
  {
    keys: ["itr12","it r12","income tax return","individual return","annual return"],
    answer: "The ITR12 is the annual Income Tax Return for individuals in South Africa. You need to submit it if you earned more than R500,000 from one employer, had multiple income sources, earned income from a business, or received a travel allowance. Filing Season opens 1 July 2025 on SARS eFiling or the MobiApp.",
  },
  {
    keys: ["provisional tax","itp","6th provisional","first provisional","second provisional"],
    answer: "Provisional tax applies if you earn income not subject to PAYE (e.g. rental, freelance, business income). You pay twice a year:\n• 1st payment: 31 August (within 6 months of tax year start)\n• 2nd payment: 28 February (at tax year end)\nA voluntary 3rd payment can be made by 30 September to avoid interest.",
  },
  {
    keys: ["filing season","when can i file","submit tax return","filing opens","filing closes","filing deadline"],
    answer: "SARS Filing Season 2025:\n• Opens: 1 July 2025\n• Closes (branch/post): 22 October 2025\n• Closes (eFiling – non-provisional): 20 November 2025\n• Closes (provisional taxpayers): 31 January 2026\nAlways file via SARS eFiling or MobiApp for fastest processing.",
  },
  {
    keys: ["documents","what documents","which documents","documents needed","required documents","upload"],
    answer: "For your ITR12 you typically need:\n• IRP5 / IT3(a) from your employer\n• Medical aid tax certificate (IT3(b))\n• Retirement annuity certificate\n• Travel logbook (if you claimed a travel allowance)\n• Bank statements for interest income\n• Rental income/expense records\n• Any capital gains disposals\nUpload all of these to TaxVault before filing season opens.",
  },
  {
    keys: ["medical aid","medical expenses","medical deduction","medical tax credit"],
    answer: "South Africa uses Medical Tax Credits (MTC) instead of deductions:\n• Primary member: R364/month\n• First dependant: R364/month\n• Additional dependants: R246/month each\nOut-of-pocket medical expenses above 7.5% of taxable income (or 33.3% of MTCs for under-65s) qualify as additional credits. Always upload your IT3(b) certificate.",
  },
  {
    keys: ["travel allowance","travel claim","logbook","car allowance","company car"],
    answer: "If you receive a travel allowance, you must keep a detailed logbook recording:\n• Date, odometer reading (start & end)\n• Business kilometres travelled\n• Business purpose of each trip\n80% of your travel allowance is included in taxable income by default. SARS uses fixed cost tables to calculate your actual claim. Upload your logbook to TaxVault for easy reference.",
  },
  {
    keys: ["home office","work from home","home office deduction","remote work"],
    answer: "You can claim home office expenses if:\n• You work from home for >50% of your working hours, AND\n• You have a dedicated room used exclusively for work.\nClaimable expenses include rent/bond interest, municipal rates, electricity, internet — apportioned by floor area. Your employer must confirm in writing that you work from home. Keep all invoices.",
  },
  {
    keys: ["tax brackets","tax rate","income tax rate","how much tax","sars tax table","2025 tax"],
    answer: "South Africa 2024/2025 Individual Tax Brackets:\n• R1 – R237,100: 18%\n• R237,101 – R370,500: 26%\n• R370,501 – R512,800: 31%\n• R512,801 – R673,000: 36%\n• R673,001 – R857,900: 39%\n• R857,901 – R1,817,000: 41%\n• Above R1,817,000: 45%\nPrimary rebate: R17,235. Secondary (65+): R9,444.",
  },
  {
    keys: ["capital gains","cgt","property sale","shares sold","investment"],
    answer: "Capital Gains Tax (CGT) applies when you sell assets like property, shares, or unit trusts.\n• Annual exclusion: R40,000 per individual\n• Primary residence exclusion: R2 million gain\n• Inclusion rate: 40% of net capital gain added to taxable income\nYou must declare all disposals on your ITR12. Keep purchase and sale records in TaxVault.",
  },
  {
    keys: ["retirement","ra","retirement annuity","pension","provident fund","retirement deduction"],
    answer: "Retirement fund contributions are tax-deductible up to:\n• 27.5% of the higher of taxable income or remuneration, AND\n• Maximum R350,000 per tax year\nThis includes contributions to pension, provident, and retirement annuity funds. Your fund will issue an IT3(b) certificate — upload it to TaxVault.",
  },
  {
    keys: ["efiling","e-filing","sars online","register efiling","how to file online"],
    answer: "SARS eFiling is the fastest way to submit your tax return:\n1. Register at efiling.sars.gov.za\n2. Add your income tax reference number\n3. Complete your ITR12 online\n4. Attach supporting documents\n5. Submit — SARS usually processes within 21 business days\nYou can also use the SARS MobiApp on iOS or Android.",
  },
  {
    keys: ["penalty","late submission","interest","underpayment","sars penalty"],
    answer: "SARS penalties for non-compliance:\n• Late submission: R250 – R16,000 per month depending on taxable income\n• Underestimation of provisional tax: 20% penalty if estimate is <90% of actual\n• Late payment: 10% of tax outstanding\n• Interest: charged at repo rate + 3.5% per annum\nAlways file on time — even if you cannot pay immediately.",
  },
  {
    keys: ["refund","tax refund","when will i get refund","sars refund","overpaid"],
    answer: "If SARS owes you a refund:\n• It is usually processed within 72 hours of assessment for simple returns\n• Complex returns: up to 21 business days\n• Your bank account must be verified with SARS\n• Check your Notice of Assessment (ITA34) on eFiling\nIf your refund is delayed, you can request a manual review via eFiling.",
  },
  {
    keys: ["rental income","rental property","airbnb","property rental"],
    answer: "Rental income is taxable and must be declared on your ITR12.\nYou can deduct:\n• Bond interest\n• Rates and taxes\n• Insurance\n• Levies (sectional title)\n• Repairs and maintenance (not improvements)\n• Agent management fees\nKeep all invoices and bank statements. Upload to TaxVault as 'Bank Statement' or 'Expense' category.",
  },
  {
    keys: ["hello","hi","hey","good morning","good afternoon","start","help","what can you do","assist"],
    answer: "Hello! I'm your TaxVault AI Tax Assistant 👋\n\nI can help you with South Africa tax questions including:\n• ITR12 filing requirements & deadlines\n• Which documents you need\n• Provisional tax payments\n• Medical aid tax credits\n• Travel allowance & logbooks\n• Home office claims\n• Capital gains tax\n• Tax brackets & rebates\n• SARS eFiling guidance\n\nWhat would you like to know?",
  },
  {
    keys: ["thank","thanks","great","perfect","helpful","awesome"],
    answer: "You're welcome! 😊 Remember to upload all your supporting documents to TaxVault before Filing Season opens on 1 July 2025. Is there anything else I can help you with?",
  },
];

function getResponse(input) {
  const q = input.toLowerCase().trim();
  for (const entry of KB) {
    if (entry.keys.some((k) => q.includes(k))) return entry.answer;
  }
  return "I'm not sure about that specific question. For official guidance, please visit sars.gov.za or consult a registered tax practitioner.\n\nI can help with: ITR12, provisional tax, filing deadlines, required documents, medical credits, travel allowance, home office, capital gains, retirement deductions, and more. Try asking one of those topics!";
}

const SUGGESTIONS = [
  "What documents do I need?",
  "When does filing season open?",
  "How does provisional tax work?",
  "Can I claim home office?",
  "What are the tax brackets?",
];

export default function TaxAssistant() {
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState("");
  const [typing, setTyping]   = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1, from: "bot",
      text: "Hi! I'm your TaxVault AI Tax Assistant 🤖\n\nAsk me anything about South African tax — ITR12, deadlines, deductions, SARS eFiling and more.",
    },
  ]);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = (text) => {
    const q = (text || input).trim();
    if (!q) return;
    setInput("");

    const userMsg = { id: Date.now(), from: "user", text: q };
    setMessages((prev) => [...prev, userMsg]);
    setTyping(true);

    setTimeout(() => {
      const answer = getResponse(q);
      setMessages((prev) => [...prev, { id: Date.now() + 1, from: "bot", text: answer }]);
      setTyping(false);
    }, 700 + Math.random() * 400);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* ── Floating Button ─────────────────────────────────────── */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all active:scale-95
          ${open ? "bg-luxury-elevated border border-luxury-border text-luxury-secondary" : "bg-luxury-cta hover:bg-luxury-cta-hover text-white"}`}
        title="AI Tax Assistant"
      >
        {open ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        )}
      </button>

      {/* ── Chat Panel ──────────────────────────────────────────── */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] max-h-[560px] flex flex-col rounded-2xl shadow-2xl border border-luxury-border bg-luxury-surface overflow-hidden">

          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-luxury-border bg-luxury-elevated flex-shrink-0">
            <div className="w-9 h-9 rounded-xl bg-luxury-cta/10 border border-luxury-cta/30 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-luxury-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-luxury-text">AI Tax Assistant</p>
              <p className="text-xs text-luxury-cta flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-luxury-cta inline-block animate-pulse" />
                South Africa Tax Expert
              </p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-lg text-luxury-muted hover:text-luxury-text hover:bg-luxury-bg transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                {msg.from === "bot" && (
                  <div className="w-7 h-7 rounded-lg bg-luxury-cta/10 border border-luxury-cta/20 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                    <svg className="w-4 h-4 text-luxury-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                )}
                <div className={`max-w-[78%] px-3 py-2.5 rounded-2xl text-xs leading-relaxed whitespace-pre-line
                  ${msg.from === "user"
                    ? "bg-luxury-cta text-white rounded-tr-sm"
                    : "bg-luxury-elevated text-luxury-text border border-luxury-border rounded-tl-sm"}`}>
                  {msg.text}
                </div>
              </div>
            ))}

            {typing && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-lg bg-luxury-cta/10 border border-luxury-cta/20 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                  <svg className="w-4 h-4 text-luxury-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="px-4 py-3 rounded-2xl bg-luxury-elevated border border-luxury-border rounded-tl-sm flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-luxury-cta animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-luxury-cta animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-luxury-cta animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 2 && !typing && (
            <div className="px-4 pb-2 flex gap-1.5 flex-wrap flex-shrink-0">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-2.5 py-1.5 rounded-full border border-luxury-gold/30 bg-yellow-50 dark:bg-luxury-elevated text-yellow-700 dark:text-luxury-gold hover:border-luxury-gold/60 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-3 border-t border-luxury-border bg-luxury-elevated flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask a SA tax question..."
                className="flex-1 resize-none rounded-xl px-3 py-2 text-xs bg-luxury-surface border border-luxury-border text-luxury-text placeholder-luxury-muted focus:outline-none focus:ring-2 focus:ring-luxury-cta/30 max-h-20"
              />
              <button
                onClick={() => send()}
                disabled={!input.trim() || typing}
                className="w-9 h-9 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
